import { useRef } from "react";
import type { ElementType, ReactNode, Ref } from "react";
import { styled } from "@pigment-css/react";
import { Pill } from "@acto/taskpane/field-sales-2/components/primitives/Pill";
import { useFlipResize } from "@acto/taskpane/field-sales-2/utils/hooks/useFlipResize";

export const TitleText = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[16],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

export const SubtitleText = styled("span")<{ tone: "accent" | "neutral" }>(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  variants: [
    {
      props: { tone: "accent" },
      style: {
        lineHeight: theme.fieldSalesTwo.font.lineHeight[120],
        color: theme.fieldSalesTwo.colors.accent.action,
        // Wrap freely; the surrounding Stack lets the pill grow vertically.
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        display: "block",
      },
    },
    {
      props: { tone: "neutral" },
      style: {
        // 1.2 rather than a tight 1: the ellipsis clip would otherwise cut the descenders.
        lineHeight: theme.fieldSalesTwo.font.lineHeight[120],
        letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
        color: theme.fieldSalesTwo.colors.neutral[800],
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
  ],
}));

const CompactTitleText = styled(TitleText)(({ theme }) => ({
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[540],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
}));

export const TitleStack = styled("span")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: theme.fieldSalesTwo.spacing[2],
  minWidth: 0,
  width: "100%",
}));

// Wraps the title/subtitle content and is the element the FLIP resize pins, so the subtitle
// wrapping stays at the final layout instead of re-wrapping while the outer pill is snapped narrow.
const ContentWrap = styled("span")({
  display: "flex",
  alignItems: "center",
  flex: "1 1 auto",
  minWidth: 0,
  width: "100%",
});

// 200px — Figma truncation cap, no token equivalent. Exported because TopBar's
// TitleSlot must clip to the same width to drive the search-expand animation.
export const TITLE_PILL_MAX_WIDTH = "12.5rem";

const TitleRoot = styled(Pill)(({ theme }) => ({
  maxWidth: TITLE_PILL_MAX_WIDTH,
  // Clip content during the FLIP snap-back frame (prev rect may be smaller
  // than the post-render natural content size, e.g., when a subtitle appears).
  overflow: "hidden",
  alignItems: "flex-start",
  paddingTop: theme.fieldSalesTwo.spacing[12],
  paddingBottom: theme.fieldSalesTwo.spacing[12],
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  '&[data-has-subtitle="true"]': {
    height: "auto",
    minHeight: theme.fieldSalesTwo.spacing[48],
    paddingBottom: theme.fieldSalesTwo.spacing[8],
  },
  // A neutral subtitle stays one line, so the pill keeps its fixed height with both lines centred.
  '&[data-has-subtitle="true"][data-subtitle-tone="neutral"]': {
    height: theme.fieldSalesTwo.spacing[48],
    minHeight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    alignItems: "center",
  },
}));

interface TitlePillProps {
  as?: ElementType;
  children: ReactNode;
  subtitle?: ReactNode;
  /** accent wraps freely and grows the pill, neutral stays one ellipsized line. */
  subtitleTone?: "accent" | "neutral";
  className?: string;
}

// A subtitle appearing, changing, or disappearing changes the pill's natural width and height;
// both animate via the FLIP resize since neither auto dimension is transitionable in plain CSS.
const ANIM_DURATION_MS = 220;
const ANIM_EASING = "cubic-bezier(0.2, 0.8, 0.2, 1)";

export function TitlePill({ as, children, subtitle, subtitleTone = "accent", className }: TitlePillProps) {
  const hasSubtitle = subtitle != null && subtitle !== false;
  const rootRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLSpanElement | null>(null);

  useFlipResize({
    elementRef: rootRef,
    contentRef,
    deps: [hasSubtitle, subtitle, children],
    durationMs: ANIM_DURATION_MS,
    easing: ANIM_EASING,
    axes: "both",
  });

  return (
    <TitleRoot
      ref={rootRef as Ref<HTMLDivElement>}
      as={as}
      variant="title"
      data-has-subtitle={hasSubtitle ? "true" : undefined}
      data-subtitle-tone={hasSubtitle ? subtitleTone : undefined}
      className={className}
    >
      <ContentWrap ref={contentRef}>
        {hasSubtitle ? (
          <TitleStack>
            {subtitleTone === "neutral" ? (
              <CompactTitleText>{children}</CompactTitleText>
            ) : (
              <TitleText>{children}</TitleText>
            )}
            <SubtitleText tone={subtitleTone}>{subtitle}</SubtitleText>
          </TitleStack>
        ) : (
          <TitleText>{children}</TitleText>
        )}
      </ContentWrap>
    </TitleRoot>
  );
}
